import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BuildingsIcon, CheckIcon, UploadSimpleIcon, TrashIcon, SignatureIcon } from "@phosphor-icons/react";

const FIELDS = [
  { k: "business_name",    l: "Business name (masthead)" },
  { k: "address",          l: "Business address (subtitle under masthead)" },
  { k: "vendor_name",      l: "Vendor / legal name" },
  { k: "gstin",            l: "Vendor GSTIN" },
  { k: "phone",            l: "Phone" },
  { k: "email",            l: "Email" },
  { k: "iocl_vendor_code", l: "IOCL vendor code" },
  { k: "iocl_po",          l: "IOCL PO number" },
  { k: "line_item",        l: "Line item / IOCL contract line" },
  { k: "company_code",     l: "Company code" },
  { k: "plant_code",       l: "Plant code" },
  { k: "currency",         l: "Currency" },
  { k: "bank_ac_no",       l: "Bank A/C number" },
  { k: "bank_ac_name",     l: "Bank A/C name" },
  { k: "bank_ifsc",        l: "IFSC code" },
];

export default function BusinessProfilePage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);
  const [sigPreview, setSigPreview] = useState(null); // blob URL from freshly picked file (before upload)
  const [sigVersion, setSigVersion] = useState(0);     // bust cache after upload/delete
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/business-profile");
        setForm(r.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load profile");
      }
    })();
  }, []);

  if (!form) return <div className="text-slate-400 overline">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/business-profile", form);
      toast.success("Business profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const onSigFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(f.type)) {
      toast.error("Signature must be a PNG, JPG or WEBP image");
      e.target.value = "";
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Signature must be smaller than 2 MB");
      e.target.value = "";
      return;
    }
    if (sigPreview) URL.revokeObjectURL(sigPreview);
    setSigPreview(URL.createObjectURL(f));
  };

  const uploadSig = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Pick a signature image first");
    setUploadingSig(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/business-profile/signature", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("signature_url", r.data.signature_url);
      setSigVersion((v) => v + 1);
      if (sigPreview) URL.revokeObjectURL(sigPreview);
      setSigPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Signature uploaded — it now prints on every invoice");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploadingSig(false); }
  };

  const removeSig = async () => {
    if (!confirm("Remove the current signature? Invoices will show a dashed line placeholder instead.")) return;
    try {
      await api.delete("/business-profile/signature");
      set("signature_url", "");
      setSigVersion((v) => v + 1);
      toast.success("Signature removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Resolve the served path against the backend
  const backendOrigin = process.env.REACT_APP_BACKEND_URL || "";
  const currentSigSrc = form.signature_url
    ? (form.signature_url.startsWith("http")
        ? `${form.signature_url}?v=${sigVersion}`
        : `${backendOrigin}${form.signature_url}?v=${sigVersion}`)
    : null;

  return (
    <div className="space-y-8" data-testid="business-profile-page">
      <div className="border-b border-slate-200 pb-6">
        <div className="overline text-brand-orange-ink flex items-center gap-2">
          <BuildingsIcon size={14} /> Settings
        </div>
        <h1 className="font-display text-4xl font-black text-brand-blue mt-2">Business profile</h1>
        <p className="text-slate-500 mt-2 max-w-2xl text-sm">
          These details print at the top and footer of every invoice / challan generated from the Invoices page.
        </p>
      </div>

      <form onSubmit={submit} className="max-w-3xl space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="overline">{f.l}</Label>
              <Input
                value={form[f.k] || ""}
                onChange={(e) => set(f.k, e.target.value)}
                className="rounded-none"
                data-testid={`profile-${f.k}`}
              />
            </div>
          ))}
        </div>
        <div>
          <Label className="overline">Footer note (thank-you paragraph)</Label>
          <textarea
            value={form.footer_note || ""}
            onChange={(e) => set("footer_note", e.target.value)}
            rows={3}
            className="w-full border border-slate-300 rounded-none px-3 py-2 focus:border-brand-blue outline-none font-mono text-xs"
            data-testid="profile-footer-note"
          />
        </div>
        <Button type="submit" disabled={saving}
          className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white"
          data-testid="profile-save">
          <CheckIcon size={14} className="mr-2" /> {saving ? "Saving…" : "Save profile"}
        </Button>
      </form>

      {/* ─── Signature uploader ─────────────────────────────────────── */}
      <section className="max-w-3xl border-t border-slate-200 pt-6 space-y-4" data-testid="signature-section">
        <div>
          <div className="overline text-brand-orange-ink flex items-center gap-2">
            <SignatureIcon size={14} /> Authorized Signatory
          </div>
          <h2 className="font-display text-2xl font-black text-brand-blue mt-1">Signature image</h2>
          <p className="text-slate-500 text-sm mt-1">
            Upload a transparent-background PNG (or JPG/WEBP) of the authorized signatory. It replaces the dashed
            line at the bottom-right of every invoice. Recommended size: 400 × 120 px, ≤ 2 MB.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="border border-slate-200 bg-slate-50 p-4 min-h-[160px] flex flex-col items-center justify-center">
            <div className="overline text-slate-400 mb-3">Current signature</div>
            {currentSigSrc ? (
              <img
                src={currentSigSrc}
                alt="signature"
                data-testid="signature-current-preview"
                className="max-h-24 max-w-full object-contain bg-white p-2 border border-slate-200"
              />
            ) : sigPreview ? (
              <>
                <img
                  src={sigPreview}
                  alt="pending signature preview"
                  data-testid="signature-pending-preview"
                  className="max-h-24 max-w-full object-contain bg-white p-2 border border-brand-orange"
                />
                <div className="overline text-brand-orange-ink mt-2 text-xs">Not uploaded yet</div>
              </>
            ) : (
              <div className="text-slate-400 text-sm italic">No signature uploaded — invoices show a dashed line.</div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <Label className="overline">Pick an image (PNG / JPG / WEBP · max 2 MB)</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onSigFilePick}
                className="w-full border border-slate-300 rounded-none px-3 py-2 text-sm bg-white file:mr-3 file:py-1 file:px-2 file:border-0 file:bg-slate-900 file:text-white file:uppercase file:text-xs"
                data-testid="signature-file-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                onClick={uploadSig}
                disabled={uploadingSig || !sigPreview}
                className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white"
                data-testid="signature-upload-btn"
              >
                <UploadSimpleIcon size={14} className="mr-2" />
                {uploadingSig ? "Uploading…" : "Upload & apply"}
              </Button>
              {currentSigSrc && (
                <Button
                  type="button"
                  onClick={removeSig}
                  variant="outline"
                  className="rounded-none border-[#FF2400] text-[#FF2400] hover:bg-red-50"
                  data-testid="signature-remove-btn"
                >
                  <TrashIcon size={14} className="mr-2" /> Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-400">
              The signature is served through this app and prints on every generated invoice / challan.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
